// apps/platform/src/lib/activityTypesClient.ts
//
// Thin wrapper around the `write-activity-type` edge function (Platform Admin
// CRUD over activity_types + activity_type_extra_fields). Reads use supabase
// directly via service-aware RLS — Platform Admins are exempted from tenant
// scoping by RLS policy `activity_types_select_platform_admin`.

import { supabase } from "./supabase";

export type BaseBehavior = "internal" | "external" | "trip" | "online";
export type ExtraFieldInputType =
  | "text_short" | "text_long" | "number" | "date"
  | "dropdown" | "yes_no" | "image" | "url";

export interface ActivityType {
  id: string;
  tenant_id: string;
  slug: string;
  label: string;
  base_behavior: BaseBehavior;
  icon: string | null;
  color: string | null;
  sort_order: number;
  enabled: boolean;
  deprecated_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ActivityTypeExtraField {
  id: string;
  activity_type_id: string;
  key: string;
  admin_label: string;
  mobile_label: string;
  input_type: ExtraFieldInputType;
  required: boolean;
  visible_to_member: boolean;
  sort_order: number;
  config: Record<string, unknown>;
  deprecated_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface TypeCreateInput {
  slug: string;
  label: string;
  base_behavior: BaseBehavior;
  icon?: string | null;
  color?: string | null;
  enabled?: boolean;
}

export type TypeUpdateInput = Partial<Omit<TypeCreateInput, "base_behavior">>;

export interface FieldCreateInput {
  activity_type_id: string;
  key: string;
  admin_label: string;
  mobile_label: string;
  input_type: ExtraFieldInputType;
  required?: boolean;
  visible_to_member?: boolean;
  sort_order?: number;
  config?: Record<string, unknown>;
}

export type FieldUpdateInput = Partial<Omit<FieldCreateInput, "activity_type_id">>;

interface EdgeResponse<T> {
  ok?: boolean;
  row?: T;
  count?: number;
  error?: string;
}

async function invoke<T>(body: Record<string, unknown>): Promise<EdgeResponse<T>> {
  const { data, error } = await supabase.functions.invoke("write-activity-type", { body });
  if (error) {
    return { error: error.message || "Falha de rede." };
  }
  return (data ?? {}) as EdgeResponse<T>;
}

// ─── Reads (RLS-gated SELECT) ───────────────────────────────────────────────

export async function listActivityTypes(tenantId: string) {
  const { data, error } = await supabase
    .from("activity_types")
    .select("*")
    .eq("tenant_id", tenantId)
    // KLUB-264 — same order as the app's chips (deriveTypesFromFeed in
    // apps/mobile/lib/typeChips.ts): sort_order, ties broken by slug. Legacy
    // rows can share a sort_order, and any other tiebreak would show the
    // table in an order the app does not use.
    .order("sort_order", { ascending: true })
    .order("slug", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as ActivityType[];
}

export async function listExtraFields(activityTypeId: string) {
  const { data, error } = await supabase
    .from("activity_type_extra_fields")
    .select("*")
    .eq("activity_type_id", activityTypeId)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as ActivityTypeExtraField[];
}

// ─── Writes (edge fn) ───────────────────────────────────────────────────────

export function createActivityType(tenantId: string, data: TypeCreateInput) {
  return invoke<ActivityType>({ op: "type.create", tenant_id: tenantId, data });
}

export function updateActivityType(id: string, data: TypeUpdateInput) {
  return invoke<ActivityType>({ op: "type.update", id, data });
}

export function deprecateActivityType(id: string) {
  return invoke<ActivityType>({ op: "type.deprecate", id });
}

// KLUB-264 — sort_order is not an input of create or update: the server
// appends a new type last, and only this reorder numbers types. It sends the
// tenant's FULL list of types (deprecated and disabled included) in the new
// order plus where "Todos" sits on that scale (null = first). One transaction
// renumbers the types 1..N and stores the position, or neither happens.
export function reorderActivityTypes(tenantId: string, ids: string[], allPosition: number | null) {
  return invoke<never>({ op: "type.reorder", tenantId, ids, allPosition });
}

export function createExtraField(tenantId: string, data: FieldCreateInput) {
  return invoke<ActivityTypeExtraField>({ op: "field.create", tenant_id: tenantId, data });
}

export function updateExtraField(id: string, data: FieldUpdateInput) {
  return invoke<ActivityTypeExtraField>({ op: "field.update", id, data });
}

export function deprecateExtraField(id: string) {
  return invoke<ActivityTypeExtraField>({ op: "field.deprecate", id });
}

export function reorderExtraFields(order: Array<{ id: string; sort_order: number }>) {
  return invoke<never>({ op: "field.reorder", order });
}
